import { useState } from "react";
import { AlertTriangle, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type ExpiresIn = "30d" | "90d" | "1y" | "never";

const expiryLabels: Record<ExpiresIn, string> = {
  "30d": "30 days",
  "90d": "90 days",
  "1y": "1 year",
  never: "No expiration",
};

function buildMcpConfig(token: string, ide: string): string {
  const apiUrl = window.location.origin;

  const env: Record<string, string> = {
    IAMU_API_URL: apiUrl,
    IAMU_API_KEY: token,
    IAMU_PROJECT_ID: "<your-project-id>",
  };

  const base = {
    mcpServers: {
      "iamu-sync": {
        command: "npx",
        args: ["-y", "@iamu-dev/iamu", "sync-server"],
        env,
      },
    },
  };

  if (ide === "cursor") {
    return `// .cursor/mcp.json\n${JSON.stringify(base, null, 2)}`;
  }
  if (ide === "windsurf") {
    return `// .windsurf/mcp.json\n${JSON.stringify(base, null, 2)}`;
  }
  return JSON.stringify(base, null, 2);
}

function CopyButton({
  text,
  label = "Copy",
  variant = "outline",
  size = "sm",
}: {
  text: string;
  label?: string;
  variant?: "outline" | "ghost" | "secondary";
  size?: "sm" | "default";
}) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  }

  return (
    <Button variant={variant} size={size} onClick={handleCopy}>
      {copied ? (
        <Check className="mr-1.5 h-3.5 w-3.5" />
      ) : (
        <Copy className="mr-1.5 h-3.5 w-3.5" />
      )}
      {copied ? "Copied" : label}
    </Button>
  );
}

function TokenCreatedDisplay({
  fullKey,
  tokenName,
  onDone,
}: {
  fullKey: string;
  tokenName: string;
  onDone: () => void;
}) {
  const [selectedIde, setSelectedIde] = useState("claude");
  const snippet = buildMcpConfig(fullKey, selectedIde);

  return (
    <div className="space-y-4 min-w-0">
      <div className="rounded-lg bg-mark-important/10 p-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-severity-important" />
          <p className="text-sm text-foreground">
            Copy this token now — it won't be shown again.
          </p>
        </div>
      </div>

      <div className="min-w-0">
        <Label className="text-xs text-muted-foreground">
          Access token for "{tokenName}"
        </Label>
        <div className="mt-1 flex items-center gap-2 min-w-0">
          <code className="flex-1 min-w-0 rounded-md bg-muted px-3 py-2 text-xs font-mono break-all select-all">
            {fullKey}
          </code>
          <CopyButton text={fullKey} label="Copy" />
        </div>
      </div>

      <div className="min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <Label className="text-xs text-muted-foreground">
            Quick setup — paste into your MCP config
          </Label>
          <div className="flex items-center gap-2">
            <Select value={selectedIde} onValueChange={setSelectedIde}>
              <SelectTrigger className="w-[136px] h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[100]">
                <SelectItem value="claude">Claude Code</SelectItem>
                <SelectItem value="cursor">Cursor</SelectItem>
                <SelectItem value="windsurf">Windsurf</SelectItem>
                <SelectItem value="generic">Generic MCP</SelectItem>
              </SelectContent>
            </Select>
            <CopyButton text={snippet} label="Copy JSON" variant="outline" />
          </div>
        </div>
        <pre className="rounded-md bg-muted p-3 text-xs leading-snug whitespace-pre-wrap break-all">
          {snippet}
        </pre>
      </div>

      <div className="flex justify-end pt-1">
        <Button onClick={onDone}>Done</Button>
      </div>
    </div>
  );
}

export function CreateTokenDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const [name, setName] = useState("");
  const [expiresIn, setExpiresIn] = useState<ExpiresIn>("90d");
  const [createdToken, setCreatedToken] = useState<{
    fullKey: string;
    name: string;
  } | null>(null);

  const createMutation = trpc.accessToken.create.useMutation({
    onSuccess: (data) => {
      setCreatedToken({ fullKey: data.fullKey, name: data.name });
      utils.accessToken.list.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to create access token"),
  });

  function handleCreate() {
    if (!name.trim()) return;
    createMutation.mutate({ name: name.trim(), expiresIn });
  }

  function handleClose() {
    setName("");
    setExpiresIn("90d");
    setCreatedToken(null);
    createMutation.reset();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {createdToken ? "Token created" : "Create access token"}
          </DialogTitle>
          <DialogDescription>
            {createdToken
              ? "Your new access token is ready to use."
              : "Access tokens authenticate the MCP server and other programmatic API access."}
          </DialogDescription>
        </DialogHeader>

        {createdToken ? (
          <TokenCreatedDisplay
            fullKey={createdToken.fullKey}
            tokenName={createdToken.name}
            onDone={handleClose}
          />
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="token-name">Name</Label>
              <Input
                id="token-name"
                placeholder='e.g., "Claude Code — MacBook Pro"'
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                }}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="token-expiry">Expiration</Label>
              <Select
                value={expiresIn}
                onValueChange={(v) => setExpiresIn(v as ExpiresIn)}
              >
                <SelectTrigger
                  id="token-expiry"
                  className="w-full max-w-full min-w-0"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent
                  position="popper"
                  className="z-[100] w-[var(--radix-select-trigger-width)]"
                >
                  {Object.entries(expiryLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!name.trim() || createMutation.isPending}
              >
                {createMutation.isPending ? "Creating..." : "Create token"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
